'use client';

import { c as _c } from "react/compiler-runtime";
import type { InterfaceToken } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { ensureError } from '@repo/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { type Address, formatUnits } from 'viem';
import { useWalletClient } from 'wagmi';
import type { VaultV2RecapSource } from '@/components/Bundler/ConfirmDialog/Recaps/hooks/useVaultV2RecapData';
import type { CheckoutEntryPoint } from '@/lib/analytics/client/events';
import VaultV2Recap from '@/components/Bundler/ConfirmDialog/Recaps/VaultV2Recap';
import { ConfirmDialogV2 } from '@/components/ConfirmDialogV2';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getWalletProvider } from '@/helpers/walletProvider';
import { invalidateUserEarnPortfolio } from '@/hooks/positions/invalidateUserEarnPortfolio';
import { buildReviewDialogParams, type CheckoutAttemptTrackingContext } from '@/lib/analytics/client/trackers/checkout-funnel';
import { trackVaultDepositCompleted } from '@/lib/analytics/client/trackers/vault-deposit';
import { getSdkTracking } from '@/lib/observability/client/trackers/transaction/sdk';
import { logger } from '@/lib/observability/logger';
import { useLogSdkBuildFailed } from '@/lib/observability/sdk';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
import { InterfaceOperation } from '@/operations/operations.types';
import { tryBuildSimulationAuthorizations } from '@/services/simulate/simulatedPosition.helpers';
import { useVaultV2SimulatedFinalPosition } from '../../../hooks/useVaultV2SimulatedFinalPosition';
import { getReviewErrorReason } from './reviewErrorReason';
import { useVaultV2DepositTransaction } from './useVaultV2DepositTransaction';
import VaultV2OperationReviewDialog from './VaultV2OperationReviewDialog';
interface VaultV2DepositReviewDialogProps {
  vaultAddress: Address;
  inputAmount: bigint;
  erc20Amount: bigint;
  nativeAmount: bigint;
  vaultAsset: InterfaceToken;
  userPosition: bigint;
  finalPosition: bigint | undefined;
  onTxSuccess: () => void;
  entryPoint: CheckoutEntryPoint;
  /** The procedure the recap reads, matching the dialog the review was opened from. */
  recapSource?: VaultV2RecapSource;
}
const VaultV2DepositReviewDialog = t0 => {
  const $ = _c(72);
  const {
    vaultAddress,
    inputAmount,
    erc20Amount,
    nativeAmount,
    vaultAsset,
    userPosition,
    finalPosition,
    onTxSuccess,
    entryPoint,
    recapSource
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const queryClient = useQueryClient();
  const {
    data: client,
    isLoading: isClientLoading
  } = useWalletClient();
  const {
    submitTransactions
  } = useTransactionContextV2();
  const {
    targetChain,
    account
  } = useWeb3Context();
  const [reviewSessionId] = useState(_temp);
  const t1 = vaultAsset?.address;
  let t2;
  if ($[0] !== erc20Amount || $[1] !== nativeAmount || $[2] !== t1 || $[3] !== vaultAddress) {
    t2 = {
      vaultAddress,
      vaultAsset: t1,
      erc20Amount,
      nativeAmount
    };
    $[0] = erc20Amount;
    $[1] = nativeAmount;
    $[2] = t1;
    $[3] = vaultAddress;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    transaction,
    requirements,
    hasRequirementsError,
    isRequirementsStale,
    requirementsError,
    isLoading
  } = useVaultV2DepositTransaction(t2);
  let t3;
  if ($[5] !== account.address || $[6] !== requirements || $[7] !== targetChain.id) {
    t3 = tryBuildSimulationAuthorizations({
      requirements,
      chainId: targetChain.id,
      from: account.address
    });
    $[5] = account.address;
    $[6] = requirements;
    $[7] = targetChain.id;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    simulationAuthorizations,
    simulationApprovalError
  } = t3;
  const t4 = requirementsError ?? simulationApprovalError;
  let t5;
  if ($[9] !== reviewSessionId || $[10] !== t4 || $[11] !== targetChain.id) {
    t5 = {
      sdkAction: "deposit",
      chainId: targetChain.id,
      reviewSessionId,
      error: t4
    };
    $[9] = reviewSessionId;
    $[10] = t4;
    $[11] = targetChain.id;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useLogSdkBuildFailed(t5);
  let t6;
  if ($[13] !== erc20Amount) {
    t6 = erc20Amount.toString();
    $[13] = erc20Amount;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== nativeAmount) {
    t7 = nativeAmount.toString();
    $[15] = nativeAmount;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = ["deposit", t6, t7];
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const simulationKey = t8;
  let t9;
  if ($[20] !== finalPosition || $[21] !== isLoading || $[22] !== simulationAuthorizations || $[23] !== simulationKey || $[24] !== transaction || $[25] !== vaultAddress || $[26] !== vaultAsset.chain.id) {
    t9 = {
      vaultAddress,
      chainId: vaultAsset.chain.id,
      transaction,
      simulationAuthorizations,
      simulationKey,
      fallbackPosition: finalPosition,
      isPreparing: isLoading
    };
    $[20] = finalPosition;
    $[21] = isLoading;
    $[22] = simulationAuthorizations;
    $[23] = simulationKey;
    $[24] = transaction;
    $[25] = vaultAddress;
    $[26] = vaultAsset.chain.id;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const {
    displayedFinalPosition,
    isUnverifiedFinalPosition,
    status: simulationStatus,
    isRefreshing: isSimulationRefreshing
  } = useVaultV2SimulatedFinalPosition(t9);
  let t10;
  if ($[28] !== account.isSafe || $[29] !== entryPoint || $[30] !== requirements || $[31] !== reviewSessionId) {
    t10 = buildReviewDialogParams({
      reviewSessionId,
      entryPoint,
      operationTypes: [InterfaceOperation.InterfaceOperationType.vaultV2Deposit],
      isSafe: account.isSafe,
      requirements
    });
    $[28] = account.isSafe;
    $[29] = entryPoint;
    $[30] = requirements;
    $[31] = reviewSessionId;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const reviewTrackingParams = t10;
  let t11;
  if ($[33] !== account || $[34] !== client || $[35] !== displayedFinalPosition || $[36] !== hasRequirementsError || $[37] !== inputAmount || $[38] !== isRequirementsStale || $[39] !== isUnverifiedFinalPosition || $[40] !== onTxSuccess || $[41] !== queryClient || $[42] !== recapSource || $[43] !== requirements || $[44] !== simulationApprovalError || $[45] !== simulationStatus || $[46] !== submitTransactions || $[47] !== targetChain || $[48] !== toggleDialog || $[49] !== transaction || $[50] !== userPosition || $[51] !== vaultAddress || $[52] !== vaultAsset) {
    t11 = checkoutTracking => {
      if (!transaction || !client || !account.isWalletTypeResolved || requirements === undefined || hasRequirementsError || isRequirementsStale || simulationStatus === "blacklist" || simulationApprovalError !== undefined) {
        return;
      }
      submitTransactions(client, {
        actionType: InterfaceOperation.InterfaceOperationType.vaultV2Deposit,
        flow: "vaultV2Deposit",
        transaction,
        ...getSdkTracking(transaction),
        requirements,
        onTxSuccess: () => {
          trackVaultDepositCompleted({
            vaultAddress,
            vaultVersion: "v2",
            assetSymbol: vaultAsset.symbol ?? "UNKNOWN",
            amountRaw: inputAmount.toString(),
            amountUsd: Number(formatUnits(vaultAsset.toUsd(inputAmount) ?? ZERO, 18)),
            chainId: targetChain.id,
            chainName: targetChain.name,
            walletType: getWalletType(account.isSafe, account.isContract),
            walletProvider: getWalletProvider(account.connector)
          });
          if (account.address) {
            invalidateUserEarnPortfolio(queryClient, account.address, "none").catch(_temp2);
          }
          onTxSuccess();
        },
        chain: targetChain,
        account,
        checkoutTracking
      }).catch(error_0 => {
        logger.error(ensureError(error_0), {
          tags: {
            component: "VaultV2DepositReviewDialog"
          }
        });
        toggleDialog(false);
      });
      toggleDialog(<ConfirmDialogV2 txMessage="Vault V2 Deposit" token={vaultAsset} operationContent={<VaultV2Recap type="vaultV2Deposit" amount={inputAmount} initialPosition={userPosition} finalPosition={displayedFinalPosition} vaultAddress={vaultAddress} isSimulationFallback={isUnverifiedFinalPosition} source={recapSource} />} />, {
        closable: false
      });
    };
    $[33] = account;
    $[34] = client;
    $[35] = displayedFinalPosition;
    $[36] = hasRequirementsError;
    $[37] = inputAmount;
    $[38] = isRequirementsStale;
    $[39] = isUnverifiedFinalPosition;
    $[40] = onTxSuccess;
    $[41] = queryClient;
    $[42] = recapSource;
    $[43] = requirements;
    $[44] = simulationApprovalError;
    $[45] = simulationStatus;
    $[46] = submitTransactions;
    $[47] = targetChain;
    $[48] = toggleDialog;
    $[49] = transaction;
    $[50] = userPosition;
    $[51] = vaultAddress;
    $[52] = vaultAsset;
    $[53] = t11;
  } else {
    t11 = $[53];
  }
  const handleContinue = t11;
  const t12 = !transaction || !client || !account.isWalletTypeResolved || account.isWrongChain || requirements === undefined || hasRequirementsError || simulationApprovalError !== undefined;
  const t13 = isRequirementsStale || isSimulationRefreshing;
  const t14 = !transaction && !isLoading;
  const t15 = !client && !isClientLoading;
  const t16 = requirementsError ?? simulationApprovalError;
  let t17;
  if ($[54] !== t14 || $[55] !== t15 || $[56] !== t16) {
    t17 = getReviewErrorReason({
      isTransactionMissing: t14,
      isWalletClientMissing: t15,
      requirementsError: t16
    });
    $[54] = t14;
    $[55] = t15;
    $[56] = t16;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== displayedFinalPosition || $[59] !== handleContinue || $[60] !== inputAmount || $[61] !== isLoading || $[62] !== isUnverifiedFinalPosition || $[63] !== recapSource || $[64] !== reviewTrackingParams || $[65] !== simulationStatus || $[66] !== t12 || $[67] !== t13 || $[68] !== t17 || $[69] !== userPosition || $[70] !== vaultAddress) {
    t18 = <VaultV2OperationReviewDialog type="vaultV2Deposit" amount={inputAmount} initialPosition={userPosition} finalPosition={displayedFinalPosition} vaultAddress={vaultAddress} isButtonDisabled={t12} isLoading={isLoading} isRefreshing={t13} handleContinue={handleContinue} reviewTrackingParams={reviewTrackingParams} simulationStatus={simulationStatus} isUnverifiedFinalPosition={isUnverifiedFinalPosition} errorReason={t17} recapSource={recapSource} />;
    $[58] = displayedFinalPosition;
    $[59] = handleContinue;
    $[60] = inputAmount;
    $[61] = isLoading;
    $[62] = isUnverifiedFinalPosition;
    $[63] = recapSource;
    $[64] = reviewTrackingParams;
    $[65] = simulationStatus;
    $[66] = t12;
    $[67] = t13;
    $[68] = t17;
    $[69] = userPosition;
    $[70] = vaultAddress;
    $[71] = t18;
  } else {
    t18 = $[71];
  }
  return t18;
};
export default VaultV2DepositReviewDialog;
function _temp() {
  return crypto.randomUUID();
}
function _temp2(error) {
  logger.error(ensureError(error), {
    tags: {
      component: "VaultV2DepositReviewDialog"
    }
  });
}